import { CalendarIcon, SearchIcon } from "@chakra-ui/icons";
import { Badge, Box, HStack, InputGroup, InputRightElement, Text, Tooltip } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import { useRouter } from "@uirouter/react";
import { z } from "zod";
import DataTable from "../../shared/components/DataTable/DataTable";
import useGraphQLDataTable from "../../shared/components/DataTable/useGraphQLDataTable";
import RangeDatePicker from "../../shared/components/DatePicker/RangeDatePicker";
import DebouncedInput from "../../shared/components/DebouncedInput";
import { EntityCardLink } from "../../shared/components/EntityCard";
import Page from "../../shared/components/Page";
import Select from "../../shared/components/Select";
import { createFilters } from "../../shared/hooks/useFilters";
import usePathParams from "../../shared/hooks/usePathParams";
import {
  CoServicePlanImportStatus,
  CoServicePlanKind,
  GetCoServicePlanImportsQueryVariables,
} from "../../shared/schema/gql/graphql";
import { ExternalPatientServicePlanId } from "../../shared/schema/schema";
import { dateFormatter } from "../../shared/utils/date-formatter";
import { enumToOptions } from "../../shared/utils/enum-to-options";
import { getFullName } from "../../shared/utils/get-full-name";
import { EntitySelect } from "../workflow/components/EntityFormControl";
import CoServicePlanImportDrawer from "./components/CoServicePlanImportDrawer";
import CoServicePlanEngineTag from "./components/CoServicePlanEngineTag";
import CoServicePlanImportStatusTag from "./components/CoServicePlanImportStatusTag";
import { hours } from "./components/servicePlanWeek.utils";
import { GET_CO_SERVICE_PLAN_IMPORTS_QUERY } from "./service-plan.graphql";
import { CoServicePlanImportRow } from "./types/service-plan-dashboard.types";

// The open import lives in the URL so a link to it can be shared. A mangled id
// just leaves the drawer closed rather than breaking the whole tab.
const zPathParams = z.object({
  servicePlanJobId: z.coerce
    .number()
    .int()
    .positive()
    .pipe(ExternalPatientServicePlanId)
    .optional()
    .catch(undefined),
});

export default function CoServicePlanImportsTab() {
  const pathParams = usePathParams(zPathParams);
  const { stateService } = useRouter();

  const setOpenImportId = (servicePlanJobId: ExternalPatientServicePlanId | null) =>
    stateService.go(".", { servicePlanJobId });

  const { dataTableProps, globalFilters, setFilter, query } = useGraphQLDataTable({
    document: GET_CO_SERVICE_PLAN_IMPORTS_QUERY,
    connection: "coServicePlanSubmissions",
    columns: columns,
    enableColumnFilters: false,
    columnVisiblity: {},
    initialSorting: [{ id: "receivedAt", desc: true }],
    globalFilters: {
      // Every import, newest first — a coordinator opens this to see what has
      // just arrived, so no status or date is pre-applied.
      initialState: { search: "" },
    },
    trProps: (row) => ({
      cursor: "pointer",
      _hover: { bg: "gray.50" },
      onClick: () => setOpenImportId(row.id),
    }),
  });

  const { createRangeDatePickerFilter } = createFilters<GetCoServicePlanImportsQueryVariables>();

  const receivedFilter = createRangeDatePickerFilter({
    label: "Received",
    startDate: { name: "receivedFrom", value: globalFilters.receivedFrom ?? null },
    endDate: { name: "receivedTo", value: globalFilters.receivedTo ?? null },
    onChange: setFilter,
  });

  const filtersNode = (
    <>
      <InputGroup width="md">
        <DebouncedInput
          debounce={200}
          placeholder="Search by patient, ID or submission ID"
          value={globalFilters.search ?? ""}
          onChange={(x) => setFilter("search", x)}
        />
        <InputRightElement>
          <SearchIcon _groupFocusWithin={{ color: "blue" }} color="gray.400" />
        </InputRightElement>
      </InputGroup>
      <Select
        label="Statuses"
        multiple={true}
        options={enumToOptions(CoServicePlanImportStatus)}
        value={globalFilters.statuses ?? null}
        onChange={(e) => setFilter("statuses", e)}
      />
      <EntitySelect
        input={{ type: "entity", entity: "Office" }}
        isDisabled={false}
        label="Office"
        multiple={true}
        renderUnselected="Office"
        value={globalFilters.officeIds ?? null}
        onChange={(officeIds) => setFilter("officeIds", officeIds)}
      />
      <InputGroup width="xs">
        <RangeDatePicker
          {...receivedFilter}
          id="co-import-received"
          inputProps={{ width: "full" }}
          placeholderText="Received"
        />
        <InputRightElement>
          <CalendarIcon _groupFocusWithin={{ color: "blue" }} color="gray.400" />
        </InputRightElement>
      </InputGroup>
    </>
  );

  return (
    <Box>
      <Page.Header>
        <Page.Title>
          Imports ({query.data?.coServicePlanSubmissions.totalCount ?? "..."})
        </Page.Title>
      </Page.Header>
      <DataTable {...dataTableProps} filterNode={filtersNode} />
      <CoServicePlanImportDrawer
        importId={pathParams.servicePlanJobId ?? null}
        onClose={() => setOpenImportId(null)}
      />
    </Box>
  );
}

const { accessor } = createColumnHelper<CoServicePlanImportRow>();

/** A count that means nothing wrong is shown grey, so the coloured ones stand out. */
function CountBadge(props: { count: number; colorScheme: string; label: string }) {
  return (
    <Tooltip label={props.label}>
      <Badge colorScheme={props.count === 0 ? "gray" : props.colorScheme}>{props.count}</Badge>
    </Tooltip>
  );
}

/**
 * One figure of the week the patient is actually getting this week, of their
 * template, and of the proposed week. The proposed side is highlighted when it
 * differs from the template, since that is the change approving would make.
 */
function ComparisonLine(props: {
  actual: number;
  current: number;
  proposed: number;
  format: (value: number) => string;
}) {
  const changed = props.current !== props.proposed;
  return (
    <Text fontSize="sm" whiteSpace="nowrap">
      {props.format(props.actual)}
      {" → "}
      {props.format(props.current)}
      {" → "}
      <Text
        as="span"
        color={changed ? "orange.500" : undefined}
        fontWeight={changed ? "semibold" : undefined}
      >
        {props.format(props.proposed)}
      </Text>
    </Text>
  );
}

const columns = [
  accessor("patient", {
    header: "Patient",
    meta: { gqlSortKey: "patientId" },
    cell: ({ getValue }) => {
      const { id, displayId, gender, status } = getValue();
      return (
        <Box w="fit-content">
          <EntityCardLink
            boxProps={{ maxWidth: "fit-content" }}
            entity={{
              type: "Patient",
              fullName: getFullName(getValue()),
              status: status,
              id: id,
              displayId: displayId ?? null,
              gender: gender ?? null,
              contactDetails: null,
            }}
            stopPropagation={true}
          />
        </Box>
      );
    },
  }),
  accessor("patient.currentOffice.name", {
    header: "Office",
    cell: ({ getValue }) => getValue() ?? "—",
  }),
  accessor("status", {
    header: "Status",
    meta: { gqlSortKey: "_status" },
    cell: ({ getValue }) => <CoServicePlanImportStatusTag status={getValue()} />,
  }),
  accessor("receivedAt", {
    header: "Received",
    meta: { gqlSortKey: "receivedAt" },
    cell: ({ getValue }) => <Text whiteSpace="nowrap">{dateFormatter.toDateTime(getValue())}</Text>,
  }),
  accessor("submissionId", {
    header: "Submission",
    meta: { gqlSortKey: "submissionId" },
    cell: ({ getValue, row }) => (
      <Box>
        <HStack spacing={2}>
          <Text fontSize="sm">{getValue()}</Text>
          {/* A past plan never has a log id or version, so the badge takes their place. */}
          {row.original.planKind === CoServicePlanKind.Past && (
            <Tooltip label="Past service plan: its plan of care is applied to the patient's history as sent and, unless a newer plan governs, a schedule starting the next Sunday is proposed for review.">
              <Badge colorScheme="purple">Past</Badge>
            </Tooltip>
          )}
        </HStack>
        {row.original.planLogId !== null && (
          <Text color="gray.500" fontSize="xs">
            {row.original.planLogId}
            {row.original.planVersion !== null && ` v${row.original.planVersion}`}
          </Text>
        )}
      </Box>
    ),
  }),
  accessor("startDate", {
    header: "Effective",
    meta: { gqlSortKey: "startDate" },
    cell: ({ getValue, row }) => {
      const startDate = getValue();
      const endDate = row.original.endDate;

      // Both null is the common case: the sender left the window to the
      // authorizations rather than naming one.
      if (startDate === null && endDate === null) {
        return <Text color="gray.500">Authorizations</Text>;
      }

      return (
        <Text whiteSpace="nowrap">
          {startDate === null ? "—" : dateFormatter.toDate(startDate)}
          {" – "}
          {endDate === null ? "—" : dateFormatter.toDate(endDate)}
        </Text>
      );
    },
  }),
  accessor("dutiesCount", {
    header: "Sent",
    meta: { gqlSortKey: "dutiesCount" },
    cell: ({ getValue, row }) => (
      <HStack spacing={1}>
        <CountBadge colorScheme="gray" count={getValue()} label="Duties sent" />
        <CountBadge
          colorScheme="gray"
          count={row.original.caregiversCount}
          label="Caregivers named"
        />
      </HStack>
    ),
  }),
  accessor("templateShiftsCount", {
    header: "Built",
    meta: { gqlSortKey: "templateShiftsCount" },
    cell: ({ getValue, row }) => (
      <HStack spacing={1}>
        <CountBadge colorScheme="blue" count={getValue()} label="Template shifts" />
        <CountBadge colorScheme="blue" count={row.original.visitsCount} label="Visits" />
      </HStack>
    ),
  }),
  // Pending only: the server leaves all six null otherwise, because once a week is
  // applied the patient's template IS the proposed one and the comparison says nothing.
  accessor("proposedWeeklyMinutes", {
    header: "Actual → Template → Proposed",
    cell: ({ row }) => {
      const {
        actualDaysCount,
        actualWeeklyMinutes,
        currentDaysCount,
        currentWeeklyMinutes,
        proposedDaysCount,
        proposedWeeklyMinutes,
      } = row.original;
      if (
        actualDaysCount === null ||
        actualWeeklyMinutes === null ||
        currentDaysCount === null ||
        currentWeeklyMinutes === null ||
        proposedDaysCount === null ||
        proposedWeeklyMinutes === null
      ) {
        return <Text color="gray.400">—</Text>;
      }

      return (
        <Tooltip label="Days scheduled and hours per week: the patient's visits this week → their current template → the proposed week">
          <Box w="fit-content">
            <ComparisonLine
              actual={actualDaysCount}
              current={currentDaysCount}
              format={(days) => `${days} ${days === 1 ? "day" : "days"}`}
              proposed={proposedDaysCount}
            />
            <ComparisonLine
              actual={actualWeeklyMinutes}
              current={currentWeeklyMinutes}
              format={(minutes) => `${hours(minutes)}/wk`}
              proposed={proposedWeeklyMinutes}
            />
          </Box>
        </Tooltip>
      );
    },
  }),
  accessor("warningsCount", {
    header: "Flags",
    meta: { gqlSortKey: "warningsCount" },
    cell: ({ getValue, row }) => (
      <HStack spacing={1}>
        <CountBadge colorScheme="yellow" count={getValue()} label="Verifier findings" />
        <CountBadge
          colorScheme="red"
          count={row.original.unstaffedCount}
          label="Duties left unstaffed"
        />
      </HStack>
    ),
  }),
  // Beside Attempts, because "how hard was this" and "what was doing the trying"
  // read together. Empty for a submission nothing has built yet.
  accessor("engine", {
    header: "Built by",
    meta: { gqlSortKey: "engine" },
    cell: ({ getValue }) => <CoServicePlanEngineTag engine={getValue()} size="sm" />,
  }),
  accessor("modelAttempts", {
    header: "Attempts",
    meta: { gqlSortKey: "modelAttempts" },
    cell: ({ getValue, row }) => {
      const retries = row.original.jobRetryCount;
      return (
        <Text whiteSpace="nowrap">
          {getValue()}
          {retries > 0 && (
            <Text as="span" color="orange.500">
              {" "}
              · {retries}/{row.original.jobMaxRetries} retries
            </Text>
          )}
        </Text>
      );
    },
  }),
];
